import React, { useEffect, useState } from "react";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, MenuItem, TextField, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import api from "../../services/api";

const encodeFile = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(",")[1]);
  reader.onerror = () => reject(new Error("No fue posible leer el archivo."));
  reader.readAsDataURL(file);
});

export default function ExistingApiRegistrationDialog({ customer, onClose }) {
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState({ originId: "", destinationId: "", password: "", rut: null, certificate: null });
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [phase, setPhase] = useState("");
  useEffect(() => {
    let active = true;
    api.get("/clients/sources/alta-rut").then(({ data }) => {
      if (active) setSources(Array.isArray(data) ? data : []);
    }).catch(() => { if (active) setError("No fue posible consultar las APIs."); });
    return () => { active = false; };
  }, []);
  const change = (key, value) => {
    setForm(previous => ({ ...previous, [key]: value }));
    setPreview(null); setConfirmed(false); setError("");
  };
  const run = async create => {
    setError("");
    if (!form.originId || !form.destinationId || form.originId === form.destinationId || !form.rut || !form.certificate || !form.password) {
      setError("Seleccione APIs distintas, adjunte el RUT y el certificado renovado con su clave."); return;
    }
    if (form.rut.size > 8 * 1024 * 1024 || form.certificate.size > 4 * 1024 * 1024) {
      setError("El RUT admite hasta 8 MB y el certificado hasta 4 MB."); return;
    }
    if (create && (!confirmed || !preview)) return;
    setBusy(true);
    setPhase(create ? "Registrando en destino. No cierre ni reenvie la solicitud..." : "Consultando origen, destino, RUT y certificado. Puede tomar unos momentos...");
    try {
      const [pdf, certificate] = await Promise.all([encodeFile(form.rut), encodeFile(form.certificate)]);
      const { data } = await api.post(`/clients/${customer.id}/existing-api-registration/${create ? "confirm" : "preview"}`, {
        originId: form.originId, destinationId: form.destinationId, pdf_base64: pdf,
        certificate_base64: certificate, certificate_password: form.password,
        ...(create ? { previewId: preview.previewId, confirm: true } : {})
      }, { timeout: 240000 });
      if (create) { setSuccess(data.message); setForm(previous => ({ ...previous, password: "", rut: null, certificate: null })); }
      else { setPreview(data); setConfirmed(false); }
    } catch (failure) {
      setError(failure.response?.data?.message || "No se confirmo la respuesta. Si estaba creando, consulte la empresa en destino antes de intentar nuevamente.");
      setPreview(null); setConfirmed(false);
    } finally { setBusy(false); setPhase(""); }
  };
  return (
    <Dialog open fullWidth maxWidth="md" onClose={busy ? undefined : onClose} disableEscapeKeyDown={busy}>
      <DialogTitle>Registrar en otra API / Retomar servicio</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle1" gutterBottom>{customer.name} · {customer.identificationNumber}</Typography>
        <Alert severity="info">Empresa ya habilitada: se conserva el Software ID y el PIN de origen para factura electronica. No se ejecutan pruebas DIAN ni se modifica la empresa de origen.</Alert>
        {error && <Alert severity="error" style={{ marginTop: 12 }}>{error}</Alert>}
        {success ? <Alert severity="success" style={{ marginTop: 12 }}>{success}</Alert> : <>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12} sm={6}><TextField id="registration-origin" select fullWidth variant="outlined" label="API de origen" value={form.originId} disabled={busy} onChange={e => change("originId", e.target.value)}>
              {sources.map(source => <MenuItem key={source.id} value={String(source.id)}>{source.name}</MenuItem>)}
            </TextField></Grid>
            <Grid item xs={12} sm={6}><TextField id="registration-destination" select fullWidth variant="outlined" label="API de destino" value={form.destinationId} disabled={busy} onChange={e => change("destinationId", e.target.value)}>
              {sources.filter(source => source.enabledForRutOnboarding && String(source.id) !== form.originId).map(source => <MenuItem key={source.id} value={String(source.id)}>{source.name}</MenuItem>)}
            </TextField></Grid>
            <Grid item xs={12} sm={6}><TextField id="registration-rut" type="file" fullWidth variant="outlined" label="RUT actualizado" InputLabelProps={{ shrink: true }} inputProps={{ accept: ".pdf" }} disabled={busy} onChange={e => change("rut", e.target.files?.[0])} /></Grid>
            <Grid item xs={12} sm={6}><TextField id="registration-certificate" type="file" fullWidth variant="outlined" label="Certificado renovado" InputLabelProps={{ shrink: true }} inputProps={{ accept: ".p12,.pfx" }} disabled={busy} onChange={e => change("certificate", e.target.files?.[0])} /></Grid>
            <Grid item xs={12} sm={6}><TextField id="registration-password" type="password" autoComplete="new-password" fullWidth variant="outlined" label="Clave del certificado" value={form.password} disabled={busy} onChange={e => change("password", e.target.value)} helperText="No se guarda en borradores ni auditorias de SOLUWHATS." /></Grid>
          </Grid>
          {preview && <div style={{ marginTop: 16 }}>
            <Typography variant="h6">Revise antes de registrar</Typography>
            <Typography>{preview.name} · NIT {preview.nit}</Typography>
            <Typography>{preview.origin} → {preview.destination}</Typography>
            <Typography>Software ID: {preview.softwareId}</Typography>
            <Typography>Ambiente: {preview.environment} · Operacion: {preview.operationCode}</Typography>
            <Typography>Certificado vigente hasta: {preview.certificateExpires}</Typography>
            <Alert severity="warning" style={{ marginTop: 12 }}>{preview.notice} No utilice simultaneamente el mismo prefijo y consecutivo en ambas APIs.</Alert>
            <FormControlLabel control={<Checkbox checked={confirmed} onChange={e => setConfirmed(e.target.checked)} disabled={busy} color="primary" />} label="Confirmo el mismo software habilitado y autorizo crear unicamente en la API de destino." />
          </div>}
        </>}
        {busy && <Typography role="status" style={{ marginTop: 12 }}>{phase}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cerrar</Button>
        {!success && <Button onClick={() => run(false)} disabled={busy} variant="outlined" color="primary">Validar sin crear</Button>}
        {!success && <Button onClick={() => run(true)} disabled={busy || !preview || !confirmed} variant="contained" color="primary">Registrar en destino</Button>}
      </DialogActions>
    </Dialog>
  );
}
